import React, { useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { KeyRound, CheckCircle2, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

/**
 * Pagina pubblica di attivazione account tramite token invito.
 * Il token viene passato in query string (?token=...) ed è single-use.
 */
export default function AcceptInvite() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [status, setStatus] = useState("form"); // form | success | error
  const [errMsg, setErrMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Le password non coincidono");
      return;
    }
    setBusy(true);
    try {
      await api.post("/access-management/accept-invite", { token, new_password: password });
      setStatus("success");
      toast.success("Account attivato");
    } catch (err) {
      setStatus("error");
      setErrMsg(err?.response?.data?.detail || "Errore attivazione");
    } finally { setBusy(false); }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow p-8 text-center">
          <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Link non valido</h2>
          <p className="text-slate-600 mb-4">Il token di invito è mancante o malformato.</p>
          <Link to="/login"><Button>Vai al login</Button></Link>
        </div>
      </div>
    );
  }

  if (status === "success") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow p-8 text-center" data-testid="accept-success">
          <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Account attivato</h2>
          <p className="text-slate-600 mb-4">Ora puoi accedere con le tue credenziali.</p>
          <Button onClick={() => navigate("/login")}>Accedi</Button>
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow p-8 text-center" data-testid="accept-error">
          <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Attivazione non riuscita</h2>
          <p className="text-slate-600 mb-4">{errMsg}</p>
          <Link to="/login"><Button variant="outline">Torna al login</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <form onSubmit={submit} className="max-w-md w-full bg-white rounded-lg shadow p-8" data-testid="accept-form">
        <div className="flex items-center gap-2 mb-6">
          <KeyRound className="w-6 h-6 text-blue-600" />
          <h2 className="text-xl font-semibold">Attiva il tuo account</h2>
        </div>
        <p className="text-slate-600 mb-6 text-sm">
          Scegli una password sicura per completare l'attivazione. Il link è a uso singolo e scade entro 72 ore.
        </p>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium">Nuova password</label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                   data-testid="accept-password" required minLength={8} />
            <p className="text-xs text-slate-500 mt-1">
              Almeno 8 caratteri, con maiuscola, minuscola, cifra e simbolo.
            </p>
          </div>
          <div>
            <label className="text-sm font-medium">Conferma password</label>
            <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                   data-testid="accept-confirm" required minLength={8} />
          </div>
        </div>
        <Button type="submit" className="w-full mt-6" disabled={busy} data-testid="accept-submit">
          {busy ? "Attivazione…" : "Attiva account"}
        </Button>
      </form>
    </div>
  );
}
